import type { PredictionMode } from '@/types/statistics';

export type TourMegaTab = 'overview' | 'indepth';
export type TourInDepthTab = 'movement' | 'shots';
/** The roster selector's two modes — the ModeRail's Players / Teams sides. */
export type TourSelectMode = 'players' | 'teams';

/**
 * A roster selection a step wants to end on, named by intent rather than by
 * player id (ids are per-segment). Resolved against the live roster by
 * `selectionTarget` in tourSelection.ts.
 *
 * - 'me'     — just the player the report belongs to (the reset)
 * - 'versus' — that player plus one opponent (one from each side of the net)
 * - 'trio'   — that player, their teammate, and one opponent
 * - 'all'    — the whole roster; demonstrated by tapping the roster's own All
 *              toggle rather than the remaining avatars
 */
export type TourSelection = 'me' | 'versus' | 'trio' | 'all';

/**
 * One tour step. Copy lives in report.json under `tour.steps.<id>` (title +
 * body), so a step definition stays a pure pointer into the report.
 */
export interface TourStep {
  /** i18n leaf: report:tour.steps.<id>.title / .body. */
  id: string;
  /** [data-tour="…"] element to spotlight. Omit for a centered card. */
  target?: string;
  /** Mega-tab the step needs — the engine taps its way there before showing. */
  megaTab?: TourMegaTab;
  /** In-Depth tab the step needs (only meaningful with megaTab 'indepth'). */
  inDepthTab?: TourInDepthTab;
  /** Roster selection the step demonstrates — the engine taps the missing
      avatars in, one at a time, before the card appears. */
  select?: TourSelection;
  /** Roster-selector mode the step needs — true taps to Teams, false to
      Players. Absolute state (not a delta), so stepping back re-establishes it.
      Only meaningful in Em Detalhe, where the ModeRail exists. A `select` step
      already pins Players (the selection setter resets the mode), so this is for
      steps that don't select — the teams demo, and its neighbours resetting it. */
  teamMode?: boolean;
  /** Shot-type chip the step needs (In Depth's Shots tab) — tapped the visible
      way like a tab. Absolute state, so stepping back re-establishes it. */
  shotType?: string;
  /** Dropped entirely for basic reports (shot data required). */
  advancedOnly?: boolean;
  /** Dropped for advanced reports — the basic-mode twin of an advancedOnly
      step, for when the same target needs different words. */
  basicOnly?: boolean;
  /** Extra px around the spotlight cutout (default in GuidedTour). */
  padding?: number;
}

/**
 * The tour, in order — THE extensibility point. To spotlight something new:
 * put `data-tour="my-id"` on the element, add an entry here, and add
 * `tour.steps.<id>` strings to report.json (en + pt). A step whose target
 * isn't in the DOM (missing data, single block, …) is skipped at runtime, so
 * entries never need data guards. Omit `target` for a modal-style step.
 *
 * Steps are re-runnable in both directions: `megaTab` / `inDepthTab` /
 * `select` describe the state the step needs, not a delta, so stepping back
 * lands on the same view the step showed going forward.
 */
export const TOUR_STEPS: TourStep[] = [
  // Asserted even though it's the default tab: a replay from the "?" button
  // can start anywhere, and the tour should open on the summary.
  { id: 'welcome', megaTab: 'overview' },
  { id: 'blocks', target: 'blocks' },
  { id: 'roster', target: 'roster', megaTab: 'overview' },
  // The Matchlytics Score opens Visão Geral, above the court. Basic reports
  // show it locked (a fogged podium), so they get their own words.
  { id: 'score', target: 'score', megaTab: 'overview', advancedOnly: true },
  { id: 'scoreBasic', target: 'score', megaTab: 'overview', basicOnly: true },
  // The court paints movement AND shot landings only on advanced reports;
  // basic ones draw the movement half alone (see overviewCourtMode), so the
  // two modes get their own words rather than one card overclaiming.
  { id: 'court', target: 'court', megaTab: 'overview', advancedOnly: true },
  { id: 'courtBasic', target: 'court', megaTab: 'overview', basicOnly: true },
  // The standouts podium ranks shot-based awards (most offensive/defensive)
  // next to movement ones. Basic reports carry no shot awards — only distance
  // and speed — so the two modes get their own words rather than one card
  // naming a prize that isn't on the board.
  { id: 'awards', target: 'awards', megaTab: 'overview', advancedOnly: true },
  { id: 'awardsBasic', target: 'awards', megaTab: 'overview', basicOnly: true },
  // Movement vitals + depth occupancy, and — on advanced reports only — the
  // net battle and the pair's sync/coverage (those bars need
  // formation_analysis, so they're absent in basic). Same split as the court
  // and awards steps: one card, two sets of words, so neither overclaims.
  {
    id: 'position',
    target: 'position',
    megaTab: 'overview',
    advancedOnly: true,
  },
  {
    id: 'positionBasic',
    target: 'position',
    megaTab: 'overview',
    basicOnly: true,
  },
  // Consistency + shot distribution — the card sits just above the tips, and
  // is shot-dependent (basic reports don't render it), so it's advanced-only.
  {
    id: 'contribution',
    target: 'contribution',
    megaTab: 'overview',
    advancedOnly: true,
  },
  // Inside that card: error rate, winner rate and winners per error, shot by
  // shot for all four players, each with its podium.
  {
    id: 'shotRaces',
    target: 'shot-races',
    megaTab: 'overview',
    advancedOnly: true,
  },
  // Style of play, the shot-type mix and the per-stroke speed estimate. Every
  // section is shot-derived, so the card never renders in basic reports.
  { id: 'shots', target: 'shots', megaTab: 'overview', advancedOnly: true },
  // Lob landings, depth/height and how many won the net back. Built from lob
  // shot records, so it's absent in basic — and skipped at runtime for a
  // player who hit none (the card hides itself on `hasData`).
  { id: 'lobs', target: 'lobs', megaTab: 'overview', advancedOnly: true },
  { id: 'tips', target: 'tips', megaTab: 'overview' },
  // Names both tabs and offers to open the other one, so it has to run on
  // Visão Geral — including when it's re-entered backwards out of Em Detalhe.
  { id: 'megaTabs', target: 'mega-tabs', megaTab: 'overview' },
  // The compare arc: the roster turns interactive in Em Detalhe, so the tour
  // demonstrates it — first reset to just the viewer, then tap in an opponent
  // (across the net), then a teammate (any mix, up to all four).
  { id: 'compare', target: 'roster', megaTab: 'indepth', select: 'me' },
  {
    id: 'compareVersus',
    target: 'roster',
    megaTab: 'indepth',
    select: 'versus',
  },
  { id: 'compareTrio', target: 'roster', megaTab: 'indepth', select: 'trio' },
  // Closes the widening arc with the shortcut for it: the All toggle takes the
  // whole court in one tap, and takes it back to the viewer alone on the next.
  { id: 'compareAll', target: 'roster', megaTab: 'indepth', select: 'all' },
  // The other half of the selector: the ModeRail flips Players → Teams, and
  // the roster reflows into the two teams to compare. No `select` (teams mode
  // is a single focus-team, not a player pick), so it declares its mode itself.
  {
    id: 'teams',
    target: 'roster',
    megaTab: 'indepth',
    teamMode: true,
  },
  // Pins Players back after the teams demo, so this step lands on the same view
  // whether it's reached forwards (from teams) or backwards (from finish).
  {
    id: 'inDepthTabs',
    target: 'indepth-tabs',
    megaTab: 'indepth',
    teamMode: false,
    advancedOnly: true,
  },
  // In the Shots tab, in page order: one stroke picked — the forehand, which
  // everyone plays — to show its average speed building up (skipped at
  // runtime when no one's forehand has a measured speed); then, back on every
  // stroke, the same error / winner charts as Visão Geral for the selection
  // (all four, from the compare arc), with each one's final value.
  {
    id: 'speedEvolution',
    target: 'speed-evolution',
    megaTab: 'indepth',
    inDepthTab: 'shots',
    teamMode: false,
    shotType: 'forehand',
    advancedOnly: true,
  },
  {
    id: 'inDepthRaces',
    target: 'indepth-races',
    megaTab: 'indepth',
    inDepthTab: 'shots',
    teamMode: false,
    shotType: 'all',
    advancedOnly: true,
  },
  // Hand the report back the way they'll want to start reading it: their own
  // stats selected, on the summary tab, every stroke in.
  { id: 'finish', megaTab: 'overview', select: 'me', shotType: 'all' },
];

/** The steps a report in the given prediction mode can show. */
export function stepsFor(predictionMode: PredictionMode): TourStep[] {
  const advanced = predictionMode === 'advanced';
  return TOUR_STEPS.filter((s) => (advanced ? !s.basicOnly : !s.advancedOnly));
}
